/**
 * Primary AFP CO Points withdrawal — by tranche order (withdrawal 1 = L1, …).
 * Replaces legacy L3 wallet / L6+ direct & team gates for primary programs.
 */

import {
  getLevelAmount,
  getRequiredDirectReferralsForLevel,
  getRequiredTeamAfpSizeForLevel,
  getTeamAfpWithdrawalRules,
} from "./autoFillProgramConfig";
import { isRootAutoFillProgram } from "../services/autoFill/autoFillProgramSpawn";

export function checkUltraPlusMembership(userProfile) {
  return userProfile?.ultraPlusStatus === "approved";
}

/** Infer tranche level from a completed withdrawal document. */
export function inferCoPointsTrancheLevel(withdrawalRow) {
  if (withdrawalRow == null) return null;
  if (withdrawalRow.coPointsLevel != null) {
    return Number(withdrawalRow.coPointsLevel);
  }
  if (withdrawalRow.programLevelAtRequest != null) {
    return Number(withdrawalRow.programLevelAtRequest);
  }
  const amt = Number(withdrawalRow.amount);
  if (!amt) return null;
  for (let L = 1; L <= 15; L += 1) {
    if (Math.abs(getLevelAmount(L) - amt) < 0.01) return L;
  }
  return null;
}

/** Attribute withdrawal rows to a specific AFP program (legacy rows → root only). */
export function filterWithdrawalsForProgram(withdrawalRows, program) {
  if (!program?.programId) return withdrawalRows || [];
  return (withdrawalRows || []).filter((w) => {
    if (w.sourceProgramId) {
      return w.sourceProgramId === program.programId;
    }
    return isRootAutoFillProgram(program);
  });
}

export function buildCompletedTrancheLevelSet(withdrawalRows, program = null) {
  const rows = program
    ? filterWithdrawalsForProgram(
        (withdrawalRows || []).filter((w) => w.status === "COMPLETED"),
        program
      )
    : (withdrawalRows || []).filter((w) => w.status === "COMPLETED");
  const levels = new Set();
  rows.forEach((row) => {
    const L = inferCoPointsTrancheLevel(row);
    if (L >= 1 && L <= 15) levels.add(L);
  });
  return levels;
}

/** Must finish withdrawal k before withdrawal L (k < L). */
export function sequentialTrancheBlockers(targetLevel, completedLevels) {
  const L = Number(targetLevel);
  const reasons = [];
  if (!Number.isFinite(L) || L < 1) return reasons;
  for (let k = 1; k < L; k += 1) {
    if (!completedLevels.has(k)) {
      reasons.push(
        `Auto-fill withdrawal ${k} (level ${k} CO tranche) must be completed before you can request withdrawal ${L}.`
      );
    }
  }
  return reasons;
}

export function primaryTrancheRequirementBlockers(
  targetLevel,
  userProfile,
  completedLevels
) {
  const L = Number(targetLevel);
  const reasons = [...sequentialTrancheBlockers(L, completedLevels)];

  if (L === 4 || L === 5) {
    if (!checkUltraPlusMembership(userProfile)) {
      reasons.push(
        "Withdrawals 4 and 5 require Ultra Plus membership (submit and get admin approval in Profile Settings)."
      );
    }
  }

  if (L >= 6) {
    if (!completedLevels.has(4)) {
      reasons.push(
        "Complete auto-fill withdrawal 4 (level 4 tranche) before withdrawals from the 6th onward."
      );
    }
    if (!completedLevels.has(5)) {
      reasons.push(
        "Complete auto-fill withdrawal 5 (level 5 tranche) before withdrawals from the 6th onward."
      );
    }
  }

  return reasons;
}

export function describePrimaryTrancheWithdrawalRules(trancheLevel) {
  const L = Number(trancheLevel);
  if (L >= 1 && L <= 3) {
    return [
      `Withdrawal ${L}: no extra conditions.`,
      L > 1
        ? `Withdrawals 1–${L - 1} must already be completed (admin approved).`
        : "First CO Points withdrawal for this program.",
    ];
  }
  if (L === 4 || L === 5) {
    return [
      `Withdrawal ${L}: Ultra Plus membership required (admin approved).`,
      `Withdrawals 1–${L - 1} must be completed before this one.`,
    ];
  }
  if (L >= 6 && L <= 15) {
    const directs = getRequiredDirectReferralsForLevel(L);
    const teamRules = getTeamAfpWithdrawalRules(L);
    const lines = [
      `Withdrawal ${L}: withdrawals 4 and 5 must be completed first.`,
      `Withdrawals 1–${L - 1} must be completed in order.`,
      `${directs} direct referral(s) with Ultra membership required.`,
    ];
    if (teamRules) {
      lines.push(
        `Team size ${teamRules.minTotal}: Ultra members at depth ${teamRules.depth} only; max ${teamRules.maxPerLeg} per level-1 leg (50% cap).`
      );
    }
    return lines;
  }
  return [];
}

/** Human-readable blockers for L6–15 (sync; counts filled in by eligibility service). */
export function describeWithdrawalSixPlusGap(
  withdrawalLevel,
  { directCount, requiredDirect, teamCount, requiredTeam, teamRules }
) {
  const L = Number(withdrawalLevel);
  const reasons = [];
  if (L < 6 || L > 15) return reasons;
  if (requiredDirect > 0 && directCount < requiredDirect) {
    reasons.push(
      `Direct Ultra members: ${directCount} / ${requiredDirect} required for withdrawal ${L}.`
    );
  }
  if (requiredTeam > 0 && teamRules && teamCount < requiredTeam) {
    reasons.push(
      `Team size (Ultra at depth ${teamRules.depth}, max ${teamRules.maxPerLeg} per L1 leg): ${teamCount} / ${requiredTeam} required for withdrawal ${L}.`
    );
  }
  return reasons;
}

export function getWithdrawalSixPlusRequirements(withdrawalLevel) {
  const L = Number(withdrawalLevel);
  if (L < 6 || L > 15) return null;
  return {
    requiredDirect: getRequiredDirectReferralsForLevel(L),
    requiredTeam: getRequiredTeamAfpSizeForLevel(L),
    teamRules: getTeamAfpWithdrawalRules(L),
  };
}
